﻿// src/components/Forum/ReportPostModal.jsx

import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { apiFetch } from "../../lib/apiFetch.js";

/**
 * ReportPostModal - Modal to report a forum post.
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - post: {
 *     id: string;
 *     content?: string;
 *     body?: string;
 *     created_at?: string;
 *     createdAt?: string;
 *     author_user_id?: string;
 *     author_email?: string;
 *     author?: { email?: string };
 *   } | null
 */
export default function ReportPostModal({ isOpen, onClose, post }) {
  const { user } = useAuth() || {};
  const currentUser = user || null;

  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setReason("");
    setError("");

    console.log("[ReportPostModal] opened", {
      post,
      currentUser,
    });
  }, [isOpen, post?.id, currentUser?.id]);

  if (!isOpen || !post) return null;

  const postContent = post.content || post.body || "";
  const disableSubmit = isSubmitting;

  const sendToast = (messageText, tone = "success") => {
    try {
      window.dispatchEvent(
        new CustomEvent("mudo:toast", {
          detail: { message: messageText, tone, ttl: 2600 },
        })
      );
    } catch {
      // no-op if toast bus not available
    }
  };

  const handleSubmit = async () => {
    console.log("[ReportPostModal] handleSubmit CLICK", {
      currentUser,
      postId: post.id,
    });

    if (!currentUser?.id) {
      setError("You must be signed in to report posts.");
      console.warn("[ReportPostModal] blocked: no currentUser");
      return;
    }

    setIsSubmitting(true);
    setError("");

    const payload = {
      // Required by backend
      messageId: post.id,
      reason,
      reporterUserId: currentUser.id,
      authorUserId:
        post.author_user_id || post.authorId || post.author_id || "unknown",

      // Extra metadata for the email
      messageContent: postContent,
      messageCreatedAt: post.created_at || post.createdAt || null,
      reporterEmail: currentUser.email || null,
      authorEmail: post.author_email || post.author?.email || null,
    };

    console.log("[ReportPostModal] sending payload", payload);

    try {
      const res = await apiFetch("/api/report-message", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      console.log("[ReportPostModal] response", res.status);

      if (!res.ok) {
        let body = null;
        try {
          body = await res.json();
        } catch {
          // ignore JSON parse errors
        }
        console.warn("[ReportPostModal] backend error body", body);
        throw new Error(body?.error || `Failed to send report (status ${res.status})`);
      }

      sendToast("Thanks, your report has been sent to the Mudo team.", "success");
      onClose?.();
    } catch (err) {
      console.error("[ReportPostModal] submit error", err);
      setError("Something went wrong while sending the report. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-white/10 backdrop-blur-xl shadow-2xl p-5 text-white">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">Report this post</h3>
            <p className="text-sm text-white/70">
              Report this post to the Mudo team if it is spam, abusive or inappropriate.
            </p>
            {!currentUser && (
              <p className="mt-1 text-xs text-amber-300">
                You need to be signed in to submit a report.
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/60 hover:text-white transition-colors text-sm"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <div className="rounded-xl border border-white/15 bg-white/5 px-3 py-2">
            <div className="text-xs uppercase tracking-wide text-white/50 mb-1">
              Post preview
            </div>
            <div className="text-sm whitespace-pre-wrap text-white/90">
              {postContent || "No content"}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm text-white/80" htmlFor="report-reason">
              Reason (optional)
            </label>
            <textarea
              id="report-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Briefly explain why you are reporting this post."
              className="w-full rounded-xl border border-white/15 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-white/30 min-h-[110px] resize-vertical"
            />
          </div>

          {error && (
            <div className="rounded-lg border border-red-400/30 bg-red-500/20 px-3 py-2 text-sm text-red-50">
              {error}
            </div>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm rounded-xl border border-white/15 bg-white/5 text-white hover:bg-white/10 transition-colors disabled:opacity-60"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black font-semibold shadow-lg shadow-emerald-500/30 disabled:opacity-70"
          >
            {isSubmitting ? "Sending…" : "Send report"}
          </button>
        </div>
      </div>
    </div>
  );
}
